import {
    compose,
    map,
    toLower,
    unnest,
    reverse,
    dropLast,
    takeLast,
    sum,
    mapObjIndexed,
    reduce,
    lt,
    takeWhile,
    __,
    partial,
    propOr,
    clone,
    toUpper,
} from 'ramda';

import {
    unnestRemoveEmpty,
    sortTakeTwoJoinDash,
    takeValues,
    formatMake,
    formatModel,
    formatYear,
    preProcessing,
    propLength,
    sortAlphaKeys,
    propValue,
    countColorsPoints,
    countNonTaxonomyPoints,
    curryComposeFour,
    withSortTermLocale,
    propTerm,
    takeTwoDashTermUpper,
    takeTwoDashTermTitleCase,
    getUniqueMakes
} from '../SharedRules';

import { RouteSegment } from '../RouteSegment';
import { Crumb } from './Crumb';
import { getUrl } from '../Engine';
import {withBaseFilters, removeBaseFilters} from '../BaseFilters';

import {routeGroups} from '../RouteGroups';
import { makeAllOfList, makeAnyOfList } from '../../__mock__/AppliedFilters.mock';

const valueTypes = {
    year: 'year',
    bodyStyles: 'bodyStyles',
    singleModel: 'singleModel',
    noModel: 'noModel',
    allModel: 'allModel',
    multiMakeModel: 'multiMakeModel',
    trims: 'trims'
};

const updatePreviousFiltersProxy = rulesState => appendFilters => {
    rulesState.crumbs.previousFilters = withBaseFilters({
        ...rulesState.crumbs.previousFilters,
        ...appendFilters
    });

    return rulesState.crumbs.previousFilters;
};

let updatePreviousFilters;

export const preProcessingCrumbs = ({selectedFilters, rulesState}) => {
    updatePreviousFilters = updatePreviousFiltersProxy(rulesState);
    if (selectedFilters.driveTypes && selectedFilters.driveTypes.terms)
    {
        selectedFilters.driveTypes.terms.forEach(x=>{x.term = toUpper(x.term)});
    }

    preProcessing({selectedFilters, rulesState});
};

const getCrumbValue = (rulesState, selectedFiltersp, type) => {
    const selectedFilters = selectedFiltersp; // have to use closure to close parameter

    let arrayResolve;

    switch(type) {
        case valueTypes.trims:
        case valueTypes.year:
            arrayResolve = urlResult => [
                new Crumb(`${formatYear(rulesState.crumbs.yearsData.year, rulesState.crumbs.yearsData.isYearRange)}`, urlResult, selectedFilters)
            ];
            break;
        case valueTypes.bodyStyles:
            arrayResolve = urlResult => [
                new Crumb(`${withSortTermLocale(rulesState.crumbs.bodyStylesData)[0].term}`, urlResult, selectedFilters)
            ];
            break;
        case valueTypes.singleModelParentMake:
            arrayResolve = urlResult => [
                new Crumb(`${formatMake(rulesState.firstMake)}`, urlResult, selectedFilters)
            ];
            break;
        case valueTypes.singleModel:
            arrayResolve = urlResult => [
                new Crumb(`${formatMake(rulesState.firstMake)} ${formatModel(rulesState.firstModelName)}`, urlResult, selectedFilters)
            ];
            break;
        case valueTypes.allModel:
        case valueTypes.noModel:
            arrayResolve = urlResult => [
                new Crumb(`${formatMake(rulesState.firstMake)}`, urlResult, selectedFilters)
            ];
            break;
        case valueTypes.multiMakeModel:
            arrayResolve = urlResult => [
                new Crumb(rulesState.firstModelName
                    ? `${formatMake(rulesState.firstMake)} ${formatModel(rulesState.firstModelName)}`
                    : `${formatMake(rulesState.firstMake)}`, urlResult, selectedFilters)
            ];
            break;
        default:
            break;
    }

    return getUrl(selectedFilters).then(arrayResolve);
};

const hasMinMax = obj => obj && obj.min && obj.max;

const isValidYear = year => year
    && year.ranges
    && year.ranges.length
    && Object.keys(year.ranges[0]).length;

const hasYearRange = year => {
    const proper = isValidYear(year)
        && hasMinMax(year.ranges[0]);

    const firstYear = year.ranges[0];

    return proper
        && firstYear.min
        && firstYear.max
        && firstYear.min !== firstYear.max;
};

export const getYearsCrumb = ({data: year, rulesState}) => {
    const selectedFilters = updatePreviousFilters({year});
    const isYearRange = hasYearRange(year)
    rulesState.crumbs.yearsData = {year: year.ranges[0], isYearRange};

    return RouteSegment.getSegmentWhen(
        isValidYear(year),
        ()=> getCrumbValue(rulesState, selectedFilters, valueTypes.year),
        routeGroups.levelOne,
        isYearRange ? 2 : 1);
};

export const getBodyStyleCrumb = ({data, data: {terms: bodyStyles}, rulesState}) => {
    const selectedFilters = updatePreviousFilters({bodyStyles: data});

    rulesState.crumbs.bodyStylesData = bodyStyles;

    return RouteSegment.getSegmentWhen(
        bodyStyles && bodyStyles.length,
        () => getCrumbValue(rulesState, selectedFilters, valueTypes.bodyStyles),
        routeGroups.taxonomy
    );
};

export const getCrumbForSingleModelParentMakeSelect = ({modelIds, rulesState}) => {
    const makes = getUniqueMakes(modelIds);
    const firstKey = makes && makes.length && makes[0];
    const tempFilters = {
        modelIds: {
            ...modelIds,
            models: modelIds.models.map(x=>({
                ...x
            }))
        }
    };

    if (firstKey) {
        // force all models;
        tempFilters.modelIds.models.forEach(x=>{
            if(x.make === firstKey){
                x.isEntireMakeSelected = true;
            }
        });
    }

    const selectedFilters = updatePreviousFilters(tempFilters);

    return RouteSegment.getSegmentWhen(
        rulesState.flags.isSingleModel && firstKey,
        ()=> getCrumbValue(rulesState, selectedFilters, valueTypes.singleModelParentMake),
        routeGroups.taxonomy
    );
}

export const getCrumbForSingleModelSelect = ({modelIds, rulesState}) => {
    const selectedFilters = updatePreviousFilters({modelIds: {...modelIds}});

    return RouteSegment.getSegmentWhen(
        rulesState.flags.isSingleModel,
        ()=> getCrumbValue(rulesState, selectedFilters, valueTypes.singleModel),
        routeGroups.taxonomy
    );
}

export const getCrumbForAllModelSelect = ({modelIds, rulesState}) => {
    const selectedFilters = updatePreviousFilters({modelIds: {...modelIds}});

    return RouteSegment.getSegmentWhen(
        rulesState.flags.isAllModels,
        () => getCrumbValue(rulesState, selectedFilters, valueTypes.allModel),
        routeGroups.taxonomy
    );
};

export const getCrumbForNoModelSelect = ({modelIds, rulesState}) => {
    const selectedFilters = updatePreviousFilters({modelIds: {...modelIds}});

    return RouteSegment.getSegmentWhen(
        rulesState.flags.isNoModels,
        () => getCrumbValue(rulesState, selectedFilters, valueTypes.noModel),
        routeGroups.taxonomy);
};

export const getCrumbForMultiMakeModel = ({modelIds, rulesState}) => {
    const selectedFilters = updatePreviousFilters({modelIds: {...modelIds}});

    return RouteSegment.getSegmentWhen(
        rulesState.flags.isMultiMake || rulesState.flags.isMultiModel,
        () => getCrumbValue(rulesState, selectedFilters, valueTypes.multiMakeModel),
        routeGroups.taxonomy
    );
};

const pushDownCategory = mapObjIndexed((value, key) => (
    (value && value.terms && value.terms.length) ? [...value.terms.map(x=> {
        return {...x, category: key}
    })] : []
));

const hasOptions = obj => compose(lt(0), sum, map(propLength), map(propOr([], 'terms')), map(x=> obj[`${x}`]), Object.keys);
const unnestSortTerm = compose(withSortTermLocale, unnest);
const concatReducer = reduce((acc, e) => [...acc, e], []);
const extractOptionsList = obj => compose(concatReducer, unnestSortTerm, map(x=> obj[`${x}`]), Object.keys);

export const getOptionsCrumb = ({data: options, rulesState}) => {
    const optionsKeys = options && sortAlphaKeys(options);
    const hasKeys = optionsKeys.length;
    const sumLengths = hasOptions(options);

    const hasLengths = hasKeys && sumLengths(options);

    const isValid = hasKeys && hasLengths;

    let optionsUrlPromiseList = [];

    if (isValid) {
        const optionsWithCategory = pushDownCategory(options);
        const categorizedOptions = extractOptionsList(optionsWithCategory)(optionsWithCategory);

        let optionsClone = [...categorizedOptions];
        let smallerOptions = {...options};

        optionsUrlPromiseList = categorizedOptions.reverse().map(c => {
            const terms = [...optionsClone].map(propTerm);
            const selectedFilters = withBaseFilters({
                ...rulesState.crumbs.previousFilters,
                ...smallerOptions
            });

            optionsClone = dropLast(1, optionsClone);

            smallerOptions = {
                ...smallerOptions,
                [`${c.category}`]: makeAnyOfList(
                    [...smallerOptions[`${c.category}`].terms.filter(x=>x.term!==c.term)]
                )
            };

            return {
                terms,
                selectedFilters,
                getUrl: getUrl(clone(selectedFilters), rulesState.config.selectedFiltersForApi)
            };
        });
    }

    updatePreviousFilters({options});

    const reversedList = reverse(optionsUrlPromiseList);

    const valuef = () => Promise.all(reversedList.map(x=>x.getUrl)).then(results => {
        return results.map((result,i)=> {
            return new Crumb(`${sortTakeTwoJoinDash(takeLast(1, reversedList[i].terms))}`, result, reversedList[i].selectedFilters)
        });
    });

    return RouteSegment.getSegmentWhen(isValid, () => valuef(), routeGroups.options, options.length );
};

const getCrumbPromise = (rulesState, {clonedItems}, propertyName, isAllOfList) => () => {
    const tempList = [...clonedItems];

    const selectedFilters = withBaseFilters({
        ...rulesState.crumbs.previousFilters,
        [propertyName]: isAllOfList
            ? makeAllOfList(clonedItems)
            : makeAnyOfList(clonedItems)
    });

    clonedItems = dropLast(1, clonedItems);

    return {
        tempList,
        selectedFilters,
        getUrl: getUrl(selectedFilters, rulesState.config.selectedFiltersForApi)
    };
};

export const getItemizedCrumb = (propertyName, routeGroup, transformUpper, isAllOfList, {data: itemsListInput, rulesState}) => {
    const {terms: itemsList} = itemsListInput;

    const isValid = itemsList && itemsList.length;

    const orderedItems = withSortTermLocale(itemsList);

    const cloneList = {clonedItems: [...orderedItems]};

    const urlPromiseList = orderedItems.map(getCrumbPromise(rulesState, cloneList, propertyName, isAllOfList));

    updatePreviousFilters({
        [propertyName]: isAllOfList
            ? makeAllOfList(itemsList)
            : makeAnyOfList(itemsList)
    });

    const reversedList = reverse(urlPromiseList);

    const valuef = () => Promise.all(reversedList.map(x=>x.getUrl)).then(results => {
        return results.map((result,i)=> {
            const last = takeLast(1, reversedList[i].tempList);

            const text = transformUpper
                ? `${takeTwoDashTermUpper(last)}`
                : `${takeTwoDashTermTitleCase(last)}`


            return new Crumb(text, result, reversedList[i].selectedFilters)
        });
    });

    return RouteSegment.getSegmentWhen(isValid, () => valuef(), routeGroup, itemsList.length );
}

// partially applied functions
const getColorCrumbPartial = partial(getItemizedCrumb, ['colors', routeGroups.colors, false, false]);
const getFeaturesCrumbPartial = partial(getItemizedCrumb, ['features', routeGroups.features, false, true]);
const getTrimsCrumbPartial = partial(getItemizedCrumb, ['trims', routeGroups.trims, true, false]);

export const getColorCrumb = (...params) => getColorCrumbPartial(...params);
export const getFeaturesCrumb = (...params) => getFeaturesCrumbPartial(...params);
export const getTrimsCrumb = (...params) => getTrimsCrumbPartial(...params);

export const getLocationCrumb = () =>  RouteSegment.getSegmentWhen(false, () => {}, routeGroups.location );

const mapResults = map(x=> {
    return {
        ...x,
        urlResult: {
            ...x.urlResult,
            pathname: toLower(x.urlResult.pathname)
        },
        url: toLower(x.urlResult.pathname)
    };
});

const finalizeCrumbs = crumbs => {
    return {
        crumbs: crumbs.map(x=>({
            ...x,
            selectedFilters: removeBaseFilters(x.selectedFilters)
        }))
    };
};

const formatSegments = curryComposeFour(mapResults, __, unnest, takeValues);
const takeUntilCvnaid = compose(takeWhile(x => x.urlResult && !x.urlResult.cvnaid))
const takeAll = compose(takeWhile(() => true));
const formatTakeUntilCvnaid = formatSegments(takeUntilCvnaid);
const formatTakeAll = formatSegments(takeAll);
const finishFormattingTakeAll = compose(finalizeCrumbs, formatTakeAll, unnest);
const finishFormattingTakeUntilCvnaid = compose(finalizeCrumbs, formatTakeUntilCvnaid, unnest);

export const postProcessingCrumbs = ({results, rulesState}) => {
    const properResults = unnestRemoveEmpty(results);

    return Promise.all(properResults.map(propValue)).then(values=> {
        const nonTaxonomyPoints = countNonTaxonomyPoints(results);

        rulesState.flags.nonTaxonomyPoints = nonTaxonomyPoints;
        rulesState.flags.colorsPoints = countColorsPoints(results);

        values.forEach((x,i)=> {
            properResults[i].value = x
        });

        rulesState.flags.hasCvnaid = rulesState.requiresCvnaid();

        return {
            ...(rulesState.config.allowCvnaid
                ? finishFormattingTakeAll(properResults)
                : finishFormattingTakeUntilCvnaid(properResults))

        };

    });
}
